import axios from "axios";
import api from "./api";
import {
  ValidateDocumentParams,
  ValidateLivenessParams,
} from "../interfaces/validateAllow";

export async function validateDevice(body: any) {
  return api.post("/allowme/device/validate", body);
}

export async function invalidateDevice(token: string, protocol: string) {
  return api.post("/allowme/invalidate", { token, protocol });
}

type Base64File = {
  base64: string;
  contentType?: string;
  fileName?: string;
};

export function base64ToBlob(
  input: string | Blob | Base64File,
  fallbackMime = "image/jpeg"
): Blob {
  if (input instanceof Blob) {
    return input;
  }

  if (typeof input === "object" && "base64" in input) {
    const { base64, contentType } = input;
    return base64ToBlob(base64, contentType || fallbackMime);
  }

  let b64 = input as string;
  let mime = fallbackMime;

  const commaIdx = b64.indexOf(",");
  if (b64.startsWith("data:") && commaIdx !== -1) {
    const meta = b64.slice(5, b64.indexOf(";"));
    mime = meta || fallbackMime;
    b64 = b64.slice(commaIdx + 1);
  }

  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

  return new Blob([bytes], { type: mime });
}

const apiForm = axios.create({ 
  baseURL: api.defaults.baseURL,
  timeout: 120000 // 2 minutes timeout
});

export async function validateLiveness(params: ValidateLivenessParams) {
  const payload = {
    integration_name: params.integrationName,
    user_id: params.userId,
    user_ip: params.userIp || undefined,
    event: params.event,
    metadata: { cpf: params.cpf, name: params.name },
    protocol: params.protocol,
  };

  const form = new FormData();
  form.append("payload", JSON.stringify(payload));

  (params.images ?? []).slice(0, 4).forEach((img, idx) => {
    let blob: Blob;
    if (img instanceof Blob) {
      blob = img;
    } else {
      blob = base64ToBlob(img);
    }
    form.append(`image${idx + 1}`, blob, `image${idx + 1}.jpg`);
  });

  return apiForm.post("/allowme/liveness/validate", form);
}

export async function validateDocument(params: ValidateDocumentParams) {
  return api.post("/allowme/document/validate", {
    integrationName: params.integrationName,
    userId: params.userId,
    userIp: params.userIp ?? undefined,
    event: params.event,
    metadata: {
      cpf: params.cpf,
      name: params.name,
    },
    protocol: params.protocol,
    images: params.images,
  });
}

export async function getSerasaToken() {
  return api.post("AllowMe/serasa/token");
}

export async function sendSerasaCadastralAuthenticationFromStates(
  selfieImage: any,
  protocol?: string,
  cpf?: string
) {
  const { data: serasaToken } = await api.post("AllowMe/serasa/token");

  const selfieBlob = base64ToBlob(
    selfieImage,
    selfieImage?.contentType || "image/jpeg"
  );

  const form = new FormData();
  if (selfieBlob) {
    form.append("Selfie", selfieBlob, pickFileName(selfieImage, "selfie.jpg"));
  }
  if (protocol) {
    form.append("Protocol", protocol);
  }
  if (cpf) form.append("Cpf", cpf);   
  form.append("Token", serasaToken);

  return apiForm.post("AllowMe/serasa/cadastral-authentication-1-n", form);
}

function pickFileName(obj: any, fallback = "file.bin") {
  if (obj?.fileName) return obj.fileName;
  const mime: string | undefined = obj?.contentType;
  if (mime === "application/pdf") return "document.pdf";
  if (mime?.startsWith("image/")) return "document.jpg";
  return fallback;
}

export async function sendSerasaFaceMatchFromStates(
  documentImage: any,
  selfieImage: any,
  protocol?: string,
  cpf?: string
) {
  const { data: serasaToken } = await api.post("AllowMe/serasa/token");

  const docBlob = base64ToBlob(
    documentImage,
    documentImage?.contentType || "image/jpeg"
  );
  const selfieBlob = base64ToBlob(
    selfieImage,
    selfieImage?.contentType || "image/jpeg"
  );

  const form = new FormData();
  if (docBlob) {
    form.append(
      "Document",
      docBlob,
      pickFileName(documentImage, "document.jpg")
    );
  }
  if (selfieBlob) {
    form.append("Selfie", selfieBlob, pickFileName(selfieImage, "selfie.jpg"));
  }
  if (protocol) {
    form.append("Protocol", protocol);
  }
  if (cpf) {
    form.append("Cpf", cpf);
  }
  form.append("Token", serasaToken);

  return apiForm.post("AllowMe/serasa/facematch", form);
}

export async function sendSerasaDocumentoscopyFromStates(documentImage: any, protocol?: string, cpf?: string) {
  const { data: serasaToken } = await api.post("AllowMe/serasa/token");

  const docBlob = base64ToBlob(
    documentImage,
    documentImage?.contentType || "image/jpeg"
  );

  const form = new FormData();
  if (docBlob) {
    form.append("Document", docBlob, pickFileName(documentImage, "document.jpg"));
  }
  if (protocol) {
    form.append("Protocol", protocol);
  }
  if (cpf) form.append("Cpf", cpf);
  form.append("Token", serasaToken);

  return apiForm.post("AllowMe/serasa/documentoscopia", form);
}

export async function sendSerproFaceBureauFromStates(
  selfieImage: any,
  protocol?: string,
  cpf?: string
) {
  const selfieBlob = base64ToBlob(
    selfieImage,
    selfieImage?.contentType || "image/jpeg"
  );

  const form = new FormData();
  if (selfieBlob) {
    form.append("Selfie", selfieBlob, pickFileName(selfieImage, "selfie.jpg"));
  }
  if (protocol) {
    form.append("Protocol", protocol);
  }
  if (cpf) {
    form.append("Cpf", cpf);
  }

  return apiForm.post("AllowMe/serpro/face-bureau", form);
}